/**
 * positiveTrendCorrection — pure helpers for the OF-129 "never show a negative
 * Spinach growth trend to customers" presentation hack.
 *
 * When a plot's leaf-size trend is negative (last measurement < first), these
 * helpers reorder the VISIBLE datapoints ascending so the trend/forecast reads
 * as non-decreasing. Dates stay fixed; only the values/payloads are permuted.
 *
 * Rules for callers:
 *   - Frontend DISPLAY only. Never feed the output of these functions into
 *     anything persisted to the backend (raw measurements/forecasts stay intact).
 *   - Gate every use behind `isPositiveTrendCorrectionEnabled` so admins and
 *     non-opted-in clients keep seeing uncorrected data.
 *   - Feed in series that already exclude hidden datapoints.
 *
 * The reorder algorithm is intentionally centralised here so it can be swapped
 * (e.g. to a capped-growth or isotonic fit) in one place later.
 */

const allFinite = (values) => values.every((v) => Number.isFinite(v));

/**
 * True when the series has >= 2 numeric points and the last is smaller than the
 * first (i.e. endpoint growth `last - first` is negative).
 */
export const endpointGrowthIsNegative = (values) => {
  if (!Array.isArray(values) || values.length < 2) return false;
  const first = values[0];
  const last = values[values.length - 1];
  if (!Number.isFinite(first) || !Number.isFinite(last)) return false;
  return last < first;
};

/**
 * Whole-record correction keyed on ONE field, keeping each record's date field
 * pinned to its original chronological slot. If `field`'s endpoint growth is
 * negative, sort the records ascending by `field` and reassign the sorted
 * payloads onto the date-ordered slots — every OTHER field of a record moves
 * together, so cross-field invariants are preserved (e.g. an 80-95 percentile
 * stays >= its own average, and a stdDev stays paired with its own mean). Use
 * this instead of reordering two correlated metrics independently, which can
 * make the lines cross. Records MUST already be sorted by date. Returns the
 * original list unchanged when no correction is needed or `field` isn't numeric.
 */
export const reorderRecordsByFieldKeepingDate = (
  records,
  field,
  dateKey = "date",
) => {
  if (!Array.isArray(records) || records.length < 2) return records;
  const values = records.map((r) => r?.[field]);
  if (!allFinite(values) || !endpointGrowthIsNegative(values)) return records;
  const sorted = [...records].sort((a, b) => a[field] - b[field]);
  return records.map((r, i) => ({ ...sorted[i], [dateKey]: r[dateKey] }));
};

/**
 * Produce a DISPLAY copy of a `{ [greenhouse]: forecastRow[] }` map: for every
 * row that carries a `corrected` payload (attached at build time when the trend
 * was negative and correction was enabled), swap in the corrected
 * `lastData`/`day1..day5` and drop the `corrected` key. Rows without `corrected`
 * pass through untouched. Returns the SAME reference when nothing changed, so
 * downstream memoisation/caching does not thrash when the feature is off.
 *
 * The raw map (used by the persistence path) must never be passed through this
 * on its way to the backend.
 */
export const applyCorrectedForecastForDisplay = (summaryForecastData) => {
  if (!summaryForecastData || typeof summaryForecastData !== "object") {
    return summaryForecastData;
  }
  let changed = false;
  const out = {};
  for (const gh of Object.keys(summaryForecastData)) {
    const rows = summaryForecastData[gh];
    if (!Array.isArray(rows)) {
      out[gh] = rows;
      continue;
    }
    out[gh] = rows.map((row) => {
      if (!row?.corrected) return row;
      changed = true;
      const { corrected, ...rest } = row;
      return { ...rest, ...corrected };
    });
  }
  return changed ? out : summaryForecastData;
};

/** Min/max of a numeric series (ignores the rest); {} when not enough data. */
export const seriesMinMax = (values) => {
  const nums = (values || []).filter((v) => Number.isFinite(v));
  if (nums.length === 0) return { min: undefined, max: undefined };
  return { min: Math.min(...nums), max: Math.max(...nums) };
};

/**
 * Gate: correction runs only for non-admin users on a site+crop that has opted
 * in via `metadata[cropName].FORCE_POSITIVE_LEAF_TREND`.
 * @param {boolean|Function} isAdmin - UserContext isAdmin (fn) or a boolean
 * @param {object} site - currentSite (with .metadata)
 * @param {string} cropName - currentCrop?.name
 */
export const positiveTrendFlagOn = (cropMetadata) => {
  const flag = cropMetadata?.FORCE_POSITIVE_LEAF_TREND;
  return flag === true || flag === "true";
};

export const isPositiveTrendCorrectionEnabled = ({ isAdmin, site, cropName }) => {
  const admin = typeof isAdmin === "function" ? isAdmin() : isAdmin;
  if (admin) return false;
  return positiveTrendFlagOn(site?.metadata?.[cropName]);
};
